import { axiosInstance } from "../../axios-Instance";
import { toast } from "react-toastify";
import { successAlert, toastOptions } from "../../utils";
import { useContext } from "react";
import { AuthContext } from "../../context";
import { getLoginToken, setLoginToken } from "../../storage";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LoginProps, RegisterProps } from "../../interface";
import { queryKeys } from "../../react-query/constants";

const SERVER_ERROR = "There was an error contacting the server.";

async function userLogout() {
  try {
    const data = await axiosInstance({
      url: "/auth/logout",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getLoginToken()}`,
      },
    });

    return data?.data;
  } catch (error) {
    localStorage.clear();
  }
}
async function userLogin(formData: LoginProps) {
  const data = await axiosInstance({
    url: "/auth/login",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return data?.data;
}
async function googleLogin(formData: LoginProps) {
  const data = await axiosInstance({
    url: "/auth/google",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return data?.data;
}
async function createAdmin(formData: LoginProps) {
  const data = await axiosInstance({
    url: "/auth/admin",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return data?.data;
}

async function microsoftLogin(formData: LoginProps) {
  const data = await axiosInstance({
    url: "/auth/microsoft",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return data?.data;
}
async function allUsers() {
  const data = await axiosInstance({
    url: "/auth",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data?.data;
}
async function userRegister(formData: RegisterProps) {
  try {
    const response = await axiosInstance({
      url: "/auth",
      method: "POST",
      data: formData,
    });

    return response.data;
  } catch (error) {
    console.error("Error occurred during user registration:", error);
    throw error;
  }
}

async function forgotPassword(formData: any) {
  try {
    const data = await axiosInstance({
      url: "/auth/forgotPassword",
      method: "POST",
      data: formData,
      headers: {
        "Content-Type": "application/json",
      },
    });

    return data?.data;
  } catch (error) {
    console.error("Error occurred during user registration:", error);
    throw error;
  }
}
async function deleteAccount(formData: any) {
  try {
    const data = await axiosInstance({
      url: `/auth/${formData}`,
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getLoginToken()}`,
      },
    });

    return data?.data;
  } catch (error) {
    console.error("Error occurred during user registration:", error);
    throw error;
  }
}
async function resetPassword(formData: any) {
  try {
    const data = await axiosInstance({
      url: `/auth/resetpassword/${formData["token"]}`,
      method: "PUT",
      data: formData,
      headers: {
        "Content-Type": "application/json",
      },
    });

    return data?.data;
  } catch (error) {
    console.error("Error occurred during user registration:", error);
    throw error;
  }
}

async function updateUser(formData: any) {
  try {
    const response = await axiosInstance({
      url: `/auth/me`,
      method: "PUT",
      data: formData,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getLoginToken()}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error occurred during user registration:", error);
    throw error;
  }
}

async function updatePhoto(formData: any) {
  try {
    const response = await axiosInstance({
      url: `/auth/photo`,
      method: "POST",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${getLoginToken()}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error occurred during user registration:", error);
    throw error;
  }
}

async function approveAccount(formData: any) {
  try {
    const response = await axiosInstance({
      url: `/auth/approve/${formData["_id"]}`,
      method: "PUT",
      data: formData,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getLoginToken()}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error occurred during user registration:", error);
    throw error;
  }
}

export function useLogin() {
  const authCtx = useContext(AuthContext);

  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => userLogin(formData as any),
    onSuccess: (data) => {
      setLoginToken(data.token);
      authCtx.authenticate(data.token);
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useGoogleLogin() {
  const authCtx = useContext(AuthContext);
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => googleLogin(formData as any),
    onSuccess: (data) => {
      setLoginToken(data.token);
      authCtx.authenticate(data.token);
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      //   toast.error(err, toastOptions);
      // showAlert({
      //   type: "error",
      //   title: "Ooops",
      //   text: err,
      // });
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useMicrosoftLogin() {
  const authCtx = useContext(AuthContext);
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => microsoftLogin(formData as any),
    onSuccess: (data) => {
      setLoginToken(data.token);
      authCtx.authenticate(data.token);
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useCreateAdmin() {
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => createAdmin(formData as any),
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useRegister() {
  const authCtx = useContext(AuthContext);
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => userRegister(formData as any),
    onSuccess: (data) => {
      setLoginToken(data.token);
      authCtx.authenticate(data.token);
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => updateUser(formData as any),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.user]);
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useUpdatePhoto() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => updatePhoto(formData as any),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.user]);
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => deleteAccount(formData as any),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries([queryKeys.users]);
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useApproveAccount() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => approveAccount(formData as any),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries([queryKeys.users]);
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}
export function useForgotPassword() {
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => forgotPassword(formData),
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useResetPassword() {
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => resetPassword(formData),
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useUsers() {
  const fallback: any = [];
  const { data = fallback } = useQuery({
    queryKey: [queryKeys.users],
    queryFn: allUsers,
  });
  return data;
}

async function updateProfile(formData: any) {
  const data = await axiosInstance({
    url: `/auth/${formData["_id"]}`,
    method: "PUT",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data;
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => updateProfile(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.user]);
      successAlert("Profile updated successfully");
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

async function invite(formData: any) {
  try {
    const response = await axiosInstance({
      url: `/invite`,
      method: "POST",
      data: formData,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getLoginToken()}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error occurred during user registration:", error);
    throw error;
  }
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => invite(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.user]);
      successAlert("Success");
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

async function deleteInvitedAccount(formData: any) {
  try {
    const data = await axiosInstance({
      url: `/invite/${formData}`,
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getLoginToken()}`,
      },
    });

    return data?.data;
  } catch (error) {
    console.error("Error occurred during user registration:", error);
    throw error;
  }
}

export function useDeleteInvitedUser() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => deleteInvitedAccount(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.user]);
      successAlert("Success");
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

async function updateInvPassword(formData: any) {
  try {
    const data = await axiosInstance({
      url: `/invite`,
      method: "PUT",
      data: formData,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getLoginToken()}`,
      },
    });

    return data?.data;
  } catch (error) {
    console.error("Error occurred during user registration:", error);
    throw error;
  }
}

export function useUpdatePwdInvitedUser() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => updateInvPassword(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.user]);
      successAlert("Success");
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      // toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

async function userpay(formData: any) {
  try {
    const data = await axiosInstance({
      url: `/invite/pay`,
      method: "POST",
      data: formData,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getLoginToken()}`,
      },
    });

    return data?.data;
  } catch (error) {
    console.error("Error occurred during user registration:", error);
    throw error;
  }
}

async function premium(formData: any) {
  try {
    const data = await axiosInstance({
      url: `/invite/premium`,
      method: "POST",
      data: formData,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getLoginToken()}`,
      },
    });

    return data?.data;
  } catch (error) {
    console.error("Error occurred during user registration:", error);
    throw error;
  }
}

export function useUserPayment() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => userpay(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.user]);
      successAlert("Success");
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      // toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useUserPremium() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => premium(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.user]);
      successAlert("Success");
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      // toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

async function approveKYC(formData: any) {
  const data = await axiosInstance({
    url: "/kyc/approve",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data;
}

async function rejectKYC(formData: any) {
  const data = await axiosInstance({
    url: "/kyc/reject",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data;
}

export function useApproveKyc() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => approveKYC(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.users]);
      successAlert("Success");
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      // toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useRejectKYC() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => rejectKYC(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.users]);
      successAlert("Success");
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      // toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useLogout() {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => userLogout(),
    onSuccess: (data) => {
      localStorage.clear();
      queryClient.invalidateQueries([queryKeys.user]);
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      // toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}
